import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatGermanTime } from '../../helpers/germanTime';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MouseIcon from '@mui/icons-material/Mouse';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CampaignIcon from '@mui/icons-material/Campaign';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';
import { fetchCpaPercentage } from '../../helpers/plxOrdersData';

interface CampaignSummary {
  id: number;
  name: string;
  status: string;
  productName: string;
  productCode: string;
  emailsSent: string;
  totalRecipients: number;
  totalOpens: number;
  avgOpenRate: number;
  totalClicks: number;
  avgCtr: number;
  totalUnsubscribes: number;
  totalOrders: number;
  totalRevenue: number;
  totalCpaRevenue: number;
  orderRate: number;
  createdAt: string;
  clientCodes?: string[];
  // Series linkage (added 2026-04-29)
  parentCampaignId?: number | null;
  sendNumber?: number;
  seriesRootId?: number;
}

interface ClientSummary {
  clientCode: string;
  espType: string;
  campaignCount: number;
  mailingCount: number;
  totalRecipients: number;
  totalOpens: number;
  avgOpenRate: number;
  totalClicks: number;
  avgCtr: number;
  totalOrders: number;
  avgOrderRate: number;
  totalCpaRevenue: number;
  avgCpaPerMailing: number;
  cpaEtkp: number;
  totalRevenue: number;
}

interface ClientEvaluationResponse {
  clients: ClientSummary[];
  totals: {
    mailingCount: number;
    campaignCount: number;
    totalRecipients: number;
    totalOpens: number;
    totalClicks: number;
    totalOrders: number;
    totalCpaRevenue: number;
    totalRevenue: number;
    avgOpenRate: number;
    avgCtr: number;
    avgOrderRate: number;
    avgCpaPerMailing: number;
    cpaEtkp: number;
  };
}

interface MailingNumberSummary {
  mailingNumber: number;
  mailingCount: number;
  recipients: number;
  opens: number;
  clicks: number;
  openRate: number;
  clickRate: number;
  anfoCount: number;
  cpaRevenue: number;
  cpaPerAnfo: number;
  cpaEtkp: number;
}

interface EvaluationResponse {
  campaigns: CampaignSummary[];
  summary: {
    totalCampaigns: number;
    totalRecipients: number;
    totalOpens: number;
    avgOpenRate: number;
    totalClicks: number;
    avgCtr: number;
    totalOrders: number;
    totalRevenue: number;
    totalCpaRevenue: number;
    avgOrderRate: number;
  };
  mailingNumbers: MailingNumberSummary[];
}

interface ScheduleMetrics {
  recipients: number;
  opens: number;
  openRate: number;
  clicks: number;
  ctr: number;
  unsubscribes: number;
  orders: number;
  revenue: number;
  cpaRevenue: number;
  orderRate: number;
}

interface ScheduleDetail {
  scheduleId: number;
  mailingNumber: number;
  subject: string;
  clientCode: string;
  espType: string;
  scheduledDatetime: string;
  sentAt: string | null;
  status: string;
  isNonOpener?: boolean;
  metrics: ScheduleMetrics | null;
}

interface CampaignDetailResponse {
  campaign: {
    id: number;
    name: string;
    productName: string;
    productCode: string;
    status: string;
    createdAt: string;
  };
  schedules: ScheduleDetail[];
}

// Documentation Legend Component
const DocumentationLegend: React.FC<{
  expanded: boolean;
  onToggle: () => void;
  cpaPercentage: number;
}> = ({ expanded, onToggle, cpaPercentage }) => (
  <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
    <Box
      sx={{
        p: 2,
        backgroundColor: '#f5f5f5',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      onClick={onToggle}
    >
      <Typography variant="h6" fontWeight="bold">
        Kennzahlen-Dokumentation
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {expanded ? 'Ausblenden' : 'Anzeigen'}
      </Typography>
    </Box>

    {expanded && (
      <Box sx={{ p: 3 }}>
        {/* Section 1: Basis-Metriken */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          1. Basis-Metriken (Email-Performance)
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>Empfänger:</strong> Anzahl der Emails, die an die Empfängerlisten gesendet
            wurden (aus ESP-Metriken: Mailchimp + Iterable)
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Öffnungen:</strong> Anzahl der einzigartigen Email-Öffnungen
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Öffnungsrate:</strong> (Öffnungen / Empfänger) × 100 - Prozentsatz der
            Empfänger, die die Email geöffnet haben
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Klicks:</strong> Anzahl der einzigartigen Klicks auf Links in der Email
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>CTR (Click-Through Rate):</strong> (Klicks / Öffnungen) × 100 - Prozentsatz der
            Öffner, die geklickt haben
          </Typography>
        </Box>

        {/* Section 2: ANFOs */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          2. ANFOs (Anforderungen)
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>ANFOs:</strong> Anzahl der Nutzer, die über die Email-Kampagne eine Bestellung
            aufgegeben haben
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Identifikation: Über Banner-Pattern im Tracking-Link (SAM_ID-
            {'{mailing_id}'})
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Filter: Abgelehnte ANFOs (vertrag_sts = &apos;V&apos;) werden ausgeschlossen
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Pro Kunde: Ein ANFO pro Ordernumber (COUNT DISTINCT)
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Anfos je Klick:</strong> (Anfos ÷ Klicks) × 100 — Prozentsatz der Klicker, die
            bestellt haben
          </Typography>
        </Box>

        {/* Section 3: Umsatz */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          3. Umsatz (real)
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>Umsatz (real):</strong> Tatsächlicher Zahlungseingang auf dem Firmenkonto
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Berechnung: Summe von <code>plx_order_metrics.actual_revenue</code> (= zahlung_brutto
            aus PLX)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Berücksichtigt automatisch: Stornos, Teilzahlungen, Rücklastschriften,
            Zahlungsausfälle
          </Typography>
          <Typography
            variant="body2"
            sx={{ pl: 2, fontStyle: 'italic', color: '#d32f2f' }}
            paragraph
          >
            WICHTIG: Umsatz = 0 € für neue Kampagnen (0-30 Tage) ist NORMAL - Kunden sind noch in
            Testphase.
          </Typography>
          <Typography
            variant="body2"
            sx={{ pl: 2, fontStyle: 'italic', color: '#2e7d32' }}
            paragraph
          >
            Für Kampagnen-Bewertung: CPA € verwenden! Umsatz dient dem späteren Vergleich der
            tatsächlichen Einnahmen.
          </Typography>
        </Box>

        {/* Section 4: CPA */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          4. CPA (Cost Per Acquisition) - Marketing-Budget-Anteil
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>CPA ist keine Ist-Kosten-Zahl</strong> — sondern der Teil des Anfo-Werts, der
            dem Marketing-Kanal zugerechnet werden darf.
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Kostenstruktur intern: <strong>{100 - cpaPercentage} % Anfo-Wert</strong> decken
            Produktion (Texter, PM, Börsenbrief-Autoren, Infrastruktur),{' '}
            <strong>{cpaPercentage} % sind Marketing-Budget</strong> (E-Mail, Ads,
            Webinar-Produktion, Affiliates).
          </Typography>
          <Typography variant="body2" sx={{ pl: 2 }} paragraph>
            → Daher: <code>anfo_cpa = anfo_wert × {cpaPercentage} %</code> — das Maximal-Budget pro
            Lead, damit sich Akquise wirtschaftlich rechnet. Aktueller Split konfigurierbar unter{' '}
            <code>/plx-orders → Produkte → CPA-Prozentsatz-Slider</code>.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>CPA-Umsatz:</strong> Summe der CPA-Beträge aller Bestellungen dieser Kampagne.
            Zeigt wie viel das Mailing zur Deckung seines eigenen Marketing-Budgets beigetragen hat.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>CPA € / Empfänger:</strong> CPA-Umsatz / Empfänger — der pro-Mail-Anteil am
            Marketing-Budget. Liegt dieser Wert über den tatsächlichen Versand-/Produktions-Kosten
            pro Mail → <strong>profitabel</strong>, darunter → drauf gezahlt.
          </Typography>
        </Box>

        {/* Section 5: KPI-Leitfaden */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          5. KPI-Leitfaden nach Kampagnenalter
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" paragraph>
            Phase 1: Erste 7 Tage (ENGAGEMENT-FOKUS)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, color: '#2e7d32' }} paragraph>
            Diese KPIs verwenden: Bestell-Rate, Email-Öffnungen/Klicks, CTR
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, color: '#d32f2f' }} paragraph>
            Diese KPIs NICHT verwenden: Umsatz (zu früh, irreführend!)
          </Typography>

          <Typography variant="body2" fontWeight="bold" paragraph sx={{ mt: 2 }}>
            Phase 2: 30-60 Tage (FRÜHE SIGNALE)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, color: '#2e7d32' }} paragraph>
            Diese KPIs verwenden: Umsatz pro Anfo (Trend), CPA-Verhältnis
          </Typography>

          <Typography variant="body2" fontWeight="bold" paragraph sx={{ mt: 2 }}>
            Phase 3: 90+ Tage (LANGZEIT-PERFORMANCE)
          </Typography>
          <Typography variant="body2" sx={{ pl: 2, color: '#2e7d32' }} paragraph>
            Diese KPIs verwenden: Umsatz pro Kunde im Vergleich zum Anfo-Wert des Produkts, Gesamt-CPA-Umsatz
          </Typography>
        </Box>

        {/* Section 6: Wichtige Hinweise */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          6. Wichtige Hinweise
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography variant="body2" paragraph>
            • <strong>Attribution:</strong> Bestellungen werden über Banner-Pattern im Tracking-Link
            zugeordnet (SAM_ID-{'{email_id}'})
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>Zeitzone:</strong> Alle Datumsangaben in CET (Central European Time)
          </Typography>
          <Typography variant="body2" paragraph>
            • <strong>Cache:</strong> Umsatz-Metriken werden alle 6 Stunden aktualisiert
            (plx_order_metrics)
          </Typography>
        </Box>
      </Box>
    )}
  </Box>
);

// Summary Card Component
const SummaryCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}> = ({ title, value, icon, color = '#1976d2' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.7, fontSize: 40 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const EvaluationTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EvaluationResponse | null>(null);
  const [clientData, setClientData] = useState<ClientEvaluationResponse | null>(null);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [selectedSeriesRoot, setSelectedSeriesRoot] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<CampaignDetailResponse | null>(null);
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [normalizeInactive, setNormalizeInactive] = useState(false);
  const [cpaPercentage, setCpaPercentage] = useState<number>(40);
  useEffect(() => {
    fetchCpaPercentage()
      .then(setCpaPercentage)
      .catch(() => {
        /* fallback to 40 */
      });
  }, []);

  // Load evaluation data
  useEffect(() => {
    loadData();
  }, [startDate, endDate, selectedClient, normalizeInactive]);

  useEffect(() => {
    loadClientData();
  }, [startDate, endDate, normalizeInactive]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedClient) params.append('clientCode', selectedClient);
      if (normalizeInactive) params.append('normalize', '1');

      const response = await fetchData<EvaluationResponse>(
        `/api/plx-mailings/evaluation/campaigns?${params.toString()}`,
      );

      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Fehler beim Laden der Auswertung');
      }
    } catch (err) {
      setError('Fehler beim Laden der Auswertung');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadClientData = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (normalizeInactive) params.append('normalize', '1');

      const response = await fetchData<ClientEvaluationResponse>(
        `/api/plx-mailings/evaluation/clients?${params.toString()}`,
      );

      if (response.success && response.data) {
        setClientData(response.data);
      }
    } catch (err) {
      console.error('Error loading client data:', err);
    }
  };

  // Campaigns are now filtered by backend when clientCode is passed.
  // Series filter is applied client-side: when a series root is selected, only
  // sends in that series are shown.
  const allCampaigns = data?.campaigns || [];
  const campaigns = useMemo(() => {
    if (!selectedSeriesRoot) return allCampaigns;
    return allCampaigns.filter((c) => (c.seriesRootId ?? c.id) === selectedSeriesRoot.id);
  }, [allCampaigns, selectedSeriesRoot]);

  // Build series rollup: GROUP BY seriesRootId across the loaded campaigns.
  // Only multi-send series shown — single-send series are equivalent to the
  // campaign row above and would just clutter.
  const seriesRollup = useMemo(() => {
    const groups = new Map<
      number,
      {
        seriesRootId: number;
        rootName: string;
        productCode: string | null;
        sendCount: number;
        totalRecipients: number;
        totalOpens: number;
        totalClicks: number;
        totalOrders: number;
        totalCpaRevenue: number;
        totalRevenue: number;
        firstCreatedAt: string;
        lastCreatedAt: string;
        sends: CampaignSummary[];
      }
    >();
    for (const c of allCampaigns) {
      const rootId = c.seriesRootId ?? c.id;
      const existing = groups.get(rootId);
      if (existing) {
        existing.sendCount += 1;
        existing.totalRecipients += c.totalRecipients || 0;
        existing.totalOpens += c.totalOpens || 0;
        existing.totalClicks += c.totalClicks || 0;
        existing.totalOrders += c.totalOrders || 0;
        existing.totalCpaRevenue += c.totalCpaRevenue || 0;
        existing.totalRevenue += c.totalRevenue || 0;
        if (c.createdAt < existing.firstCreatedAt) existing.firstCreatedAt = c.createdAt;
        if (c.createdAt > existing.lastCreatedAt) existing.lastCreatedAt = c.createdAt;
        existing.sends.push(c);
        // Prefer the actual root campaign's name (sendNumber=1) over a child's
        if ((c.sendNumber || 1) === 1 && c.id === rootId) {
          existing.rootName = c.name;
          existing.productCode = c.productCode || existing.productCode;
        }
      } else {
        groups.set(rootId, {
          seriesRootId: rootId,
          rootName: c.id === rootId ? c.name : `Series #${rootId}`,
          productCode: c.id === rootId ? c.productCode : null,
          sendCount: 1,
          totalRecipients: c.totalRecipients || 0,
          totalOpens: c.totalOpens || 0,
          totalClicks: c.totalClicks || 0,
          totalOrders: c.totalOrders || 0,
          totalCpaRevenue: c.totalCpaRevenue || 0,
          totalRevenue: c.totalRevenue || 0,
          firstCreatedAt: c.createdAt,
          lastCreatedAt: c.createdAt,
          sends: [c],
        });
      }
    }
    return Array.from(groups.values())
      .filter((g) => g.sendCount > 1)
      .sort((a, b) => (b.lastCreatedAt > a.lastCreatedAt ? 1 : -1));
  }, [allCampaigns]);

  const handleCampaignClick = async (campaign: CampaignSummary) => {
    setSelectedCampaign(campaign);
    setDetailLoading(true);
    setDetailData(null);

    try {
      const params = new URLSearchParams();
      if (selectedClient) params.append('clientCode', selectedClient);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetchData<CampaignDetailResponse>(
        `/api/plx-mailings/campaigns/${campaign.id}/evaluation${queryString}`,
      );

      if (response.success && response.data) {
        setDetailData(response.data);
      }
    } catch (err) {
      console.error('Error loading campaign details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Deep-Link `?tab=evaluation&campaign=<id>` (Bestellliste auf /plx-orders, Bestell-Detail): öffnet das
  // Detail der Kampagne, sobald sie in der geladenen Liste steht — je Id genau einmal, damit ein späteres
  // Neuladen (Datum, Mandant) es nicht wieder aufreißt. Steht sie nicht in der Liste, bleibt die Übersicht.
  const [searchParams] = useSearchParams();
  const deepLinkCampaign = Number(searchParams.get('campaign')) || null;
  const deepLinkGeoeffnet = useRef<number | null>(null);
  useEffect(() => {
    if (!deepLinkCampaign || deepLinkGeoeffnet.current === deepLinkCampaign || !data) return;
    const treffer = data.campaigns.find((c) => c.id === deepLinkCampaign);
    if (!treffer) return;
    deepLinkGeoeffnet.current = deepLinkCampaign;
    handleCampaignClick(treffer);
  }, [data, deepLinkCampaign]);

  const handleCloseDetail = () => {
    setSelectedCampaign(null);
    setDetailData(null);
  };

  // Format helpers
  const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  // Group schedules by mailing number for detail view
  const groupedSchedules = useMemo(() => {
    if (!detailData?.schedules) return [];

    const grouped: Record<
      number,
      { mailingNumber: number; subject: string; schedules: ScheduleDetail[] }
    > = {};

    detailData.schedules.forEach((s) => {
      if (!grouped[s.mailingNumber]) {
        grouped[s.mailingNumber] = {
          mailingNumber: s.mailingNumber,
          subject: s.subject,
          schedules: [],
        };
      }
      grouped[s.mailingNumber].schedules.push(s);
    });

    return Object.values(grouped).sort((a, b) => a.mailingNumber - b.mailingNumber);
  }, [detailData]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Documentation Legend */}
      <DocumentationLegend
        expanded={legendExpanded}
        onToggle={() => setLegendExpanded(!legendExpanded)}
        cpaPercentage={cpaPercentage}
      />

      {/* Date Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Von (Sendedatum)"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Bis (Sendedatum)"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Box display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={normalizeInactive}
                onChange={(e) => setNormalizeInactive(e.target.checked)}
                size="small"
              />
            }
            label="Inaktive normalisieren"
          />
          <Tooltip title="Samstags-Sends mit Inaktiven werden auf den Durchschnitt der letzten 10 normalen Sends normalisiert, um faire Vergleiche zu ermöglichen.">
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                ml: 0.5,
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      {data?.summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Kampagnen"
              value={data.summary.totalCampaigns}
              icon={<CampaignIcon fontSize="inherit" />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Empfänger"
              value={formatNumber(data.summary.totalRecipients, 0)}
              icon={<PeopleIcon fontSize="inherit" />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Öffnungen"
              value={formatNumber(data.summary.totalOpens, 0)}
              icon={<VisibilityIcon fontSize="inherit" />}
              color="#ed6c02"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø Öffnungsrate"
              value={formatPercent(data.summary.avgOpenRate)}
              icon={<EmailIcon fontSize="inherit" />}
              color="#9c27b0"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø CTR"
              value={formatPercent(data.summary.avgCtr)}
              icon={<MouseIcon fontSize="inherit" />}
              color="#0288d1"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="ANFOs"
              value={formatNumber(data.summary.totalOrders, 0)}
              icon={<ShoppingCartIcon fontSize="inherit" />}
              color="#388e3c"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Umsatz (real)"
              value={formatCurrency(data.summary.totalRevenue)}
              icon={<EuroIcon fontSize="inherit" />}
              color="#f57c00"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="CPA-Umsatz"
              value={formatCurrency(data.summary.totalCpaRevenue)}
              icon={<EuroIcon fontSize="inherit" />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="Ø Anfos je Klick"
              value={formatPercent(data.summary.avgOrderRate)}
              icon={<TrendingUpIcon fontSize="inherit" />}
              color="#7b1fa2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SummaryCard
              title="CPA ETKP"
              value={formatCurrency(
                data.summary.totalRecipients > 0
                  ? (data.summary.totalCpaRevenue / data.summary.totalRecipients) * 1000
                  : 0,
              )}
              icon={<TrendingUpIcon fontSize="inherit" />}
              color="#00897b"
            />
          </Grid>
        </Grid>
      )}

      {/* Campaign Table */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6">Kampagnen</Typography>
        {selectedClient && (
          <Chip
            label={`Filter: ${selectedClient}`}
            onDelete={() => setSelectedClient(null)}
            size="small"
            color="primary"
          />
        )}
        {selectedSeriesRoot && (
          <Chip
            label={`Series: ${selectedSeriesRoot.name}`}
            onDelete={() => setSelectedSeriesRoot(null)}
            size="small"
            color="info"
            icon={<ReplayIcon />}
          />
        )}
      </Box>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Kampagne
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Produkt
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="center"
              >
                Mails
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Empfänger
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Öffnungen
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Öffnungs-%
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Klicks
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                CTR
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                ANFOs
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                Umsatz (real)
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                CPA €
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                ANFO-%
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => (
              <TableRow
                key={campaign.id}
                hover
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
                onClick={() => handleCampaignClick(campaign)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {campaign.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(campaign.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{campaign.productName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {campaign.productCode}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{campaign.emailsSent}</Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(campaign.totalRecipients, 0)}</TableCell>
                <TableCell align="right">{formatNumber(campaign.totalOpens, 0)}</TableCell>
                <TableCell align="right">{formatPercent(campaign.avgOpenRate)}</TableCell>
                <TableCell align="right">{formatNumber(campaign.totalClicks, 0)}</TableCell>
                <TableCell align="right">{formatPercent(campaign.avgCtr)}</TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium" color="success.main">
                    {formatNumber(campaign.totalOrders, 0)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{formatCurrency(campaign.totalRevenue)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="primary.main">
                    {formatCurrency(campaign.totalCpaRevenue)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatPercent(campaign.orderRate)}</TableCell>
              </TableRow>
            ))}
            {campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography color="text.secondary" py={2}>
                    {selectedClient
                      ? `Keine Kampagnen für Client ${selectedClient} gefunden`
                      : 'Keine Kampagnen mit versendeten Mailings gefunden'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Client Overview Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Client-Übersicht
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  Client
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  ESP
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Mailings
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Kampagnen
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Empfänger
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Öffnungen
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Öffnungs-%
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Klicks
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  CTR
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  ANFOs
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  ANFO-%
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  CPA €
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Ø CPA €/Mail
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  CPA ETKP
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Umsatz (real)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientData?.clients.map((client) => (
                <TableRow
                  key={client.clientCode}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    backgroundColor: selectedClient === client.clientCode ? '#e3f2fd' : 'inherit',
                  }}
                  onClick={() =>
                    setSelectedClient(
                      selectedClient === client.clientCode ? null : client.clientCode,
                    )
                  }
                >
                  <TableCell>
                    <Typography fontWeight="medium">{client.clientCode}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {client.espType}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatNumber(client.mailingCount, 0)}</TableCell>
                  <TableCell align="right">{formatNumber(client.campaignCount, 0)}</TableCell>
                  <TableCell align="right">{formatNumber(client.totalRecipients, 0)}</TableCell>
                  <TableCell align="right">{formatNumber(client.totalOpens, 0)}</TableCell>
                  <TableCell align="right">{formatPercent(client.avgOpenRate)}</TableCell>
                  <TableCell align="right">{formatNumber(client.totalClicks, 0)}</TableCell>
                  <TableCell align="right">{formatPercent(client.avgCtr)}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium" color="success.main">
                      {formatNumber(client.totalOrders, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatPercent(client.avgOrderRate)}</TableCell>
                  <TableCell align="right">
                    <Typography color="primary.main">
                      {formatCurrency(client.totalCpaRevenue)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="primary.main">
                      {formatCurrency(client.avgCpaPerMailing)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(client.cpaEtkp)}</TableCell>
                  <TableCell align="right">{formatCurrency(client.totalRevenue)}</TableCell>
                </TableRow>
              ))}
              {/* Totals Row */}
              {clientData?.totals && (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>
                    <Typography fontWeight="bold">Gesamt</Typography>
                  </TableCell>
                  <TableCell />
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatNumber(clientData.totals.mailingCount, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatNumber(clientData.totals.campaignCount, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatNumber(clientData.totals.totalRecipients, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatNumber(clientData.totals.totalOpens, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatPercent(clientData.totals.avgOpenRate)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatNumber(clientData.totals.totalClicks, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatPercent(clientData.totals.avgCtr)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="success.main">
                      {formatNumber(clientData.totals.totalOrders, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatPercent(clientData.totals.avgOrderRate)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="primary.main">
                      {formatCurrency(clientData.totals.totalCpaRevenue)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="primary.main">
                      {formatCurrency(clientData.totals.avgCpaPerMailing)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatCurrency(clientData.totals.cpaEtkp)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {formatCurrency(clientData.totals.totalRevenue)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {(!clientData?.clients || clientData.clients.length === 0) && (
                <TableRow>
                  <TableCell colSpan={15} align="center">
                    <Typography color="text.secondary" py={2}>
                      Keine Client-Daten verfügbar
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mailing Number Evaluation */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Auswertung nach Mailing-Nummer
          {selectedClient && (
            <Chip
              label={`Filter: ${selectedClient}`}
              size="small"
              sx={{ ml: 1 }}
              onDelete={() => setSelectedClient(null)}
            />
          )}
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  Mailing #
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Anzahl
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Empfänger
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Öffnungen
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Öffnungs-%
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Klicks
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Klick-%
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  ANFOs
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  CPA €
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  Ø CPA €/ANFO
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                  align="right"
                >
                  CPA ETKP
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.mailingNumbers?.map((mn) => (
                <TableRow key={mn.mailingNumber} hover>
                  <TableCell>
                    <Typography fontWeight="medium">Mailing #{mn.mailingNumber}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatNumber(mn.mailingCount, 0)}</TableCell>
                  <TableCell align="right">{formatNumber(mn.recipients, 0)}</TableCell>
                  <TableCell align="right">{formatNumber(mn.opens, 0)}</TableCell>
                  <TableCell align="right">{formatPercent(mn.openRate)}</TableCell>
                  <TableCell align="right">{formatNumber(mn.clicks, 0)}</TableCell>
                  <TableCell align="right">{formatPercent(mn.clickRate)}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="medium" color="success.main">
                      {formatNumber(mn.anfoCount, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="primary.main">{formatCurrency(mn.cpaRevenue)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="primary.main">{formatCurrency(mn.cpaPerAnfo)}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(mn.cpaEtkp)}</TableCell>
                </TableRow>
              ))}
              {(!data?.mailingNumbers || data.mailingNumbers.length === 0) && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography color="text.secondary" py={2}>
                      Keine Mailing-Nummer-Daten verfügbar
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Series Rollup — aggregated metrics per resend series.
          Click a row to filter the campaigns table above to that series. */}
      {seriesRollup.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <ReplayIcon fontSize="small" />
            Series-Rollup
            <Tooltip title="Aggregierte Metriken über alle Sends einer Series. Nur Series mit ≥ 2 Sends werden gezeigt — Single-Send-Series stehen schon in der Kampagnen-Tabelle oben.">
              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                    Series-Root
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                    Produkt
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="center"
                  >
                    Sends
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    Empfänger
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    Öffnungen
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    Öffnungs-%
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    Klicks
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    CTR
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    ANFOs
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    ANFO-%
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    CPA €
                  </TableCell>
                  <TableCell
                    sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                    align="right"
                  >
                    Umsatz (real)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {seriesRollup.map((s) => {
                  const openRate = s.totalRecipients > 0 ? s.totalOpens / s.totalRecipients : 0;
                  const ctr = s.totalOpens > 0 ? s.totalClicks / s.totalOpens : 0;
                  const anfoRate = s.totalClicks > 0 ? s.totalOrders / s.totalClicks : 0;
                  const isSelected = selectedSeriesRoot?.id === s.seriesRootId;
                  return (
                    <TableRow
                      key={s.seriesRootId}
                      hover
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#e3f2fd' : 'inherit',
                      }}
                      onClick={() =>
                        setSelectedSeriesRoot(
                          isSelected ? null : { id: s.seriesRootId, name: s.rootName },
                        )
                      }
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {s.rootName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{s.seriesRootId} · {formatDate(s.firstCreatedAt)} →{' '}
                          {formatDate(s.lastCreatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{s.productCode || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={`${s.sendCount}×`} color="info" />
                      </TableCell>
                      <TableCell align="right">{formatNumber(s.totalRecipients, 0)}</TableCell>
                      <TableCell align="right">{formatNumber(s.totalOpens, 0)}</TableCell>
                      <TableCell align="right">{formatPercent(openRate)}</TableCell>
                      <TableCell align="right">{formatNumber(s.totalClicks, 0)}</TableCell>
                      <TableCell align="right">{formatPercent(ctr)}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium" color="success.main">
                          {formatNumber(s.totalOrders, 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatPercent(anfoRate)}</TableCell>
                      <TableCell align="right">
                        <Typography color="primary.main">
                          {formatCurrency(s.totalCpaRevenue)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(s.totalRevenue)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selectedCampaign} onClose={handleCloseDetail} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">{selectedCampaign?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCampaign?.productName} ({selectedCampaign?.productCode})
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDetail}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : detailData ? (
            <Box>
              {groupedSchedules.map((group) => (
                <Box key={group.mailingNumber} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Mailing #{group.mailingNumber}: {group.subject}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                          >
                            Client
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                          >
                            ESP
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                          >
                            Gesendet
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Empfänger
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Öffnungen
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Öffnungs-%
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Klicks
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            CTR
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            ANFOs
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            Umsatz (real)
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            CPA €
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: '#f5f5f5',
                              fontWeight: 'bold',
                            }}
                            align="right"
                          >
                            ANFO-%
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.schedules.map((schedule) => (
                          <TableRow
                            key={`${schedule.isNonOpener ? 'no-' : ''}${schedule.scheduleId}`}
                            sx={schedule.isNonOpener ? { backgroundColor: '#f0f7ff' } : {}}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography fontWeight="medium">
                                  {schedule.clientCode.toUpperCase()}
                                </Typography>
                                {schedule.isNonOpener && (
                                  <Chip
                                    size="small"
                                    label="NO"
                                    variant="outlined"
                                    color="info"
                                    sx={{ fontSize: '0.6rem', height: 18 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {schedule.espType}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  backgroundColor:
                                    schedule.status === 'sent' ? '#e8f5e9' : '#fff3e0',
                                  color: schedule.status === 'sent' ? '#2e7d32' : '#e65100',
                                }}
                              >
                                {schedule.status}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {/* sent_at ist Berlin-Wallclock — plain new Date() zeigte +2h */}
                              {schedule.sentAt
                                ? formatGermanTime(schedule.sentAt, 'DD.MM.YYYY, HH:mm:ss')
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(schedule.metrics?.recipients || 0, 0)}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(schedule.metrics?.opens || 0, 0)}
                            </TableCell>
                            <TableCell align="right">
                              {formatPercent(schedule.metrics?.openRate || 0)}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(schedule.metrics?.clicks || 0, 0)}
                            </TableCell>
                            <TableCell align="right">
                              {formatPercent(schedule.metrics?.ctr || 0)}
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="medium" color="success.main">
                                {formatNumber(schedule.metrics?.orders || 0, 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(schedule.metrics?.revenue || 0)}
                            </TableCell>
                            <TableCell align="right">
                              <Typography color="primary.main">
                                {formatCurrency(schedule.metrics?.cpaRevenue || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {formatPercent(schedule.metrics?.orderRate || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Subtotal row */}
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" fontWeight="bold">
                              Summe Mailing #{group.mailingNumber}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.recipients || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.opens || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatPercent(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.opens || 0),
                                  0,
                                ) /
                                  Math.max(
                                    group.schedules.reduce(
                                      (sum, s) => sum + (s.metrics?.recipients || 0),
                                      0,
                                    ),
                                    1,
                                  ),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.clicks || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatPercent(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.clicks || 0),
                                  0,
                                ) /
                                  Math.max(
                                    group.schedules.reduce(
                                      (sum, s) => sum + (s.metrics?.opens || 0),
                                      0,
                                    ),
                                    1,
                                  ),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="success.main">
                              {formatNumber(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.orders || 0),
                                  0,
                                ),
                                0,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatCurrency(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.revenue || 0),
                                  0,
                                ),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="primary.main">
                              {formatCurrency(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.cpaRevenue || 0),
                                  0,
                                ),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold">
                              {formatPercent(
                                group.schedules.reduce(
                                  (sum, s) => sum + (s.metrics?.orders || 0),
                                  0,
                                ) /
                                  Math.max(
                                    group.schedules.reduce(
                                      (sum, s) => sum + (s.metrics?.clicks || 0),
                                      0,
                                    ),
                                    1,
                                  ),
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}

              {/* Campaign Total - sum orders per mailing (not per schedule to avoid duplication) */}
              {groupedSchedules.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#e3f2fd',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Kampagnen-Gesamt
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Empfänger
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(
                          detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.recipients || 0),
                            0,
                          ) || 0,
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Öffnungen
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(
                          detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.opens || 0),
                            0,
                          ) || 0,
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Ø Öffnungsrate
                      </Typography>
                      <Typography variant="h6">
                        {formatPercent(
                          (detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.opens || 0),
                            0,
                          ) || 0) /
                            Math.max(
                              detailData?.schedules.reduce(
                                (sum, s) => sum + (s.metrics?.recipients || 0),
                                0,
                              ) || 1,
                              1,
                            ),
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Klicks
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(
                          detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.clicks || 0),
                            0,
                          ) || 0,
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        CTR
                      </Typography>
                      <Typography variant="h6">
                        {formatPercent(
                          (detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.clicks || 0),
                            0,
                          ) || 0) /
                            Math.max(
                              detailData?.schedules.reduce(
                                (sum, s) => sum + (s.metrics?.opens || 0),
                                0,
                              ) || 1,
                              1,
                            ),
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        ANFOs
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatNumber(
                          detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.orders || 0),
                            0,
                          ) || 0,
                          0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Umsatz (real)
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(
                          detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.revenue || 0),
                            0,
                          ) || 0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        CPA-Umsatz
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(
                          detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.cpaRevenue || 0),
                            0,
                          ) || 0,
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="caption" color="text.secondary">
                        Anfos je Klick
                      </Typography>
                      <Typography variant="h6">
                        {formatPercent(
                          (detailData?.schedules.reduce(
                            (sum, s) => sum + (s.metrics?.orders || 0),
                            0,
                          ) || 0) /
                            Math.max(
                              detailData?.schedules.reduce(
                                (sum, s) => sum + (s.metrics?.clicks || 0),
                                0,
                              ) || 1,
                              1,
                            ),
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">Keine Daten verfügbar</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EvaluationTab;
